import React, { useEffect, useState } from 'react';
import ButtonAccount from '../../../components/ButtonAccount';
import { useForm } from 'react-hook-form';
import { useTrackingContext } from '../../../context/trackingContext';
import { useUserContext } from '../../../context/userContext';
import useClient from '../../../hooks/useClient';
import config from '../../../config';
import Alert from '../../../components/Alert';

const CommunicationPreferences = () => {
  const { trackPageView, trackFormStart, trackFormSubmit, trackError } = useTrackingContext();
  const { userData, fetchUser } = useUserContext();
  const client = useClient();

  const [alert, setAlert] = useState(null);

  useEffect(() => {
    trackPageView({
      pageName: 'Account - Communication Preferences'
    });
  }, []);

  const [isEdit, setIsEdit] = useState(false);
  const {
    handleSubmit,
    register,
    formState: { isDirty, isSubmitting }
  } = useForm({
    mode: 'onTouched',
    defaultValues: {
      emailNewsletter: userData.emailNewsletter,
      emailBuildProgram: userData.emailBuildProgram,
      emailAmbassadorProgram: userData.emailAmbassadorProgram
    }
  });

  useEffect(() => {
    isDirty &&
      trackFormStart({
        formName: 'Edit communication preferences start'
      });
  }, [isDirty]);

  const onSubmit = async (data) => {
    setIsEdit(false);

    trackFormSubmit({
      formName: 'Edit communication preferences submit'
    });

    try {
      const res = await client.put(
        `${config.everywhereApi}/users/self/profile/update-marketing-preferences`,
        {
          emailNewsletter: !!data.emailNewsletter,
          emailBuildProgram: !!data.emailBuildProgram,
          emailAmbassadorProgram: !!data.emailAmbassadorProgram
        }
      );

      fetchUser();

      setAlert({ text: res.data.data.message, success: true });
    } catch (err) {
      const error = err?.response?.data?.error;

      trackError({ error_text: 'failure to update marketing preferences' });

      setAlert({
        text: error ? error?.errorMessage + ' - ' + error.errorCode : 'Unknown error',
        success: false
      });
    }
  };

  const renderButtons = () => {
    return isEdit ? (
      <div className="flex">
        <ButtonAccount disabled={!isDirty} label="Save" type="submit" />
        <ButtonAccount type="reset" label="Cancel" />
      </div>
    ) : (
      <ButtonAccount label="Edit" disabled={isSubmitting} onClick={() => setIsEdit(true)} />
    );
  };

  return (
    <div id="CommunicationPreferences">
      <div className="mb-10 mt-8">
        <div className="mb-10">
          <h2 className="font-Korolev-Bold text-xl whitespace-nowrap">Email Preferences</h2>
          <p>
            Be the first to know and receive updates, news, and promotional offers from Build a
            Rocket Boy.
          </p>
        </div>

        <form
          onSubmit={handleSubmit(onSubmit)}
          onReset={() => {
            setIsEdit(false);
          }}>
          <div className="flex w-full justify-between mb-6">
            <h2 className="font-Korolev-Bold text-xl whitespace-nowrap">Update Preferences</h2>
            <hr className="my-auto w-full align-middle mx-6 border-solid border-l-0 border-b-0 border-r-0 " />
            <div>{renderButtons()}</div>
          </div>
          {userData && (
            <div className="flex flex-col gap-4">
              <div className="flex flex-row">
                <>Newsletter</>
                <hr className="my-auto grow hidden sm:block align-middle mx-6 border-dotted border-l-0 border-b-0 border-r-0 " />
                <input
                  type="checkbox"
                  disabled={!isEdit}
                  className="accent-black ml-2"
                  {...register('emailNewsletter')}
                />
              </div>
              <div className="flex flex-row">
                <>Builder Program</>
                <hr className="my-auto grow hidden sm:block align-middle mx-6 border-dotted border-l-0 border-b-0 border-r-0 " />
                <input
                  disabled={!isEdit}
                  type="checkbox"
                  className="accent-black ml-2"
                  {...register('emailBuildProgram')}
                />
              </div>
              {userData?.isAmbassador && (
                <div className="flex flex-row">
                  <>Ambassador Program</>
                  <hr className="my-auto grow hidden sm:block align-middle mx-6 border-dotted border-l-0 border-b-0 border-r-0 " />
                  <input
                    disabled={!isEdit}
                    type="checkbox"
                    className="accent-black ml-2"
                    {...register('emailAmbassadorProgram')}
                  />
                </div>
              )}
            </div>
          )}
        </form>
      </div>

      {alert && (
        <Alert setShowAlert={setAlert} loggedIn success={alert.success}>
          <p className="pr-12 text-sm md:text-base">{alert.text}</p>
        </Alert>
      )}
    </div>
  );
};

export default CommunicationPreferences;
